import clsx from "clsx";
import { memo } from "react";
import { useDrag, useDrop } from "react-dnd";

import { ParticipantTileContent } from "components/shared/participant_tile_content";
import { useAppSelector } from "hooks/redux_hooks";
import { collect, useDragHandler } from "hooks/use_drag_handler";
import { DraggableItemType, PaneContentType, SizeType } from "shared/types";
import { classroomSelectors } from "store/index";

import "./participant.scss";

const Participant = memo(function Participant({
  userId,
  canControlClassroom,
  currentUserCanControlClassroom,
  isSelf,
  profilePhotoSize,
  signalSize,
  isSquircle,
  index,
  participantCount,
  className,
  isVertical = false,
  height,
}: {
  userId: string;
  canControlClassroom: boolean;
  currentUserCanControlClassroom: boolean;
  isSelf: boolean;
  profilePhotoSize: SizeType;
  signalSize: SizeType;
  isSquircle: boolean;
  index: number;
  participantCount: number;
  className?: string;
  isVertical?: boolean;
  height?: number;
}) {
  const [{ isDragging }, drag] = useDrag({
    type: PaneContentType.User,
    item: { id: userId },
    collect,
  });
  useDragHandler(isDragging);

  const [, drop] = useDrop({
    accept: [DraggableItemType.DirectoryUser],
    drop: async (item: { directoryCallback: (args: { userId: string }) => void }) => {
      item.directoryCallback({ userId });
    },
  });

  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );

  return (
    <div
      className={clsx(
        "participant-container bg-black relative shadow-level-3",
        {
          "participant-squircle ml7": isSquircle,
          "participant mr1": !isSquircle && !activeBreakoutSessionId,
          "participant mb1": !isSquircle && activeBreakoutSessionId,
        },
        className,
      )}
      style={isVertical ? { height: `${height}px` } : {}}
      ref={drag}
      data-testid={`participant-${userId}`}
    >
      <div className="w-full h-full" ref={drop} data-testid={`participant-${userId}-drop-target`}>
        <ParticipantTileContent
          userId={userId}
          canControlClassroom={canControlClassroom}
          currentUserCanControlClassroom={currentUserCanControlClassroom}
          isSelf={isSelf}
          profilePhotoSize={profilePhotoSize}
          signalSize={signalSize}
          isMinimal={false}
          isSquircle={isSquircle}
          index={index}
          participantCount={participantCount}
          isVertical={isVertical}
        />
      </div>
    </div>
  );
});

export { Participant };
